import React, { FC, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import {
  useForm,
  SubmitHandler,
  Control,
  UseFormSetValue,
  FieldErrors,
} from "react-hook-form";
import { styled } from "@mui/system";

import {
  onboardingFormAtom,
  organizationAtom,
  signedInUserAtom,
} from "../../state/atoms";
import {
  convertAppOrganizationSettingsToDbOrganizationSettings,
  convertDbAccountToAppAccount,
  convertDbOrganizationSettingsToAppOrganizationSettings,
  generateCustomAuthToken,
  getDbUser,
  getMyOrganization,
  updateOrganizationSettings,
} from "../../services/firebase";
import { ProfileType } from "../../types/users";
import { AccountSettings } from "../../types/organizations";
import { BouncePrevention, OnboardingSteps } from "../../types/forms";
import { DEFAULT_CUSTOM_FIELD_IDS } from "../../common/consts";
import { removeUndefinedAndNull } from "../../common/utils/data";
import { useConnectOutreach } from "../../common/hooks/useConnectOutreach";
import { makeShouldForwardProps } from "../../common/utils/ui";
import Stepper, { StepProps } from "../../components/Display/Stepper";
import { ButtonVariants } from "../../components/Display/Button";
import FormHelper, { Severity } from "../../components/DataInput/FormHelper";

import OutreachAdminPermissions from "./OutreachAdminPermissions";
import ContactAdmin from "./ContactAdmin";
import ConnectOutreach from "./ConnectOutreach";
// import ConnectHubSpot from "./ConnectHubSpot"; // HubSpot functionality excluded
import ChooseIntegration from "./ChooseIntegration";
import InviteUsers from "./InviteUsers";
import BouncePreventing from "./BouncePreventing";
import Notice from "./Notice";
import EmailStatus from "./EmailStatus";
import VerificationDate from "./VerificationDate";
import EnrichedByBoring from "./EnrichedByBoring";
import WatchVideos from "./WatchVideos";
// import { useConnectHubSpot } from "../../common/hooks/useConnectHubSpot"; // HubSpot functionality excluded

interface StepperWrapperProps {
  shouldUseDefaultHeight?: boolean;
}

const shouldForwardProp = makeShouldForwardProps<StepperWrapperProps>([
  "shouldUseDefaultHeight",
]);
const StepperWrapper = styled("div", {
  shouldForwardProp,
})<StepperWrapperProps>`
  height: ${({ shouldUseDefaultHeight }) =>
    shouldUseDefaultHeight ? "300px" : "unset"};
  width: 400px;
`;

export type FieldControllerPropsBase = {
  control: Control<AccountSettings, unknown>;
  errors: FieldErrors<AccountSettings>;
  setValue: UseFormSetValue<AccountSettings>;
};

const Onboarding: FC = () => {
  const [onboardingForm, setOnboardingForm] =
    useRecoilState(onboardingFormAtom);
  const [signedInUser, setSignedInUser] = useRecoilState(signedInUserAtom);
  const [organization, setOrganization] = useRecoilState(organizationAtom);
  
  // If user has no org, redirect to OAuth immediately - don't show onboarding
  useEffect(() => {
    if (signedInUser && !signedInUser.organizationId && !signedInUser.isOutreachAuthorized && !signedInUser.isHubSpotAuthorized) {
      const redirectToOAuth = async () => {
        try {
          const isHubSpotOrg = organization?.initialIntegrationType === "hubspot";
          if (isHubSpotOrg) {
            const redirectUri = `${window.location.origin}/oauth/hubspot`.replace(/\/$/, '');
            const { getHubSpotAuthUrl } = await import("../../services/firebase");
            const { result, status } = await getHubSpotAuthUrl(redirectUri);
            if (status === 200 && result?.hubspotAuthUrl) {
              window.location.href = result.hubspotAuthUrl;
              return;
            }
          } else {
            const { getOutreachAuthUrl } = await import("../../services/firebase");
            const { result, status } = await getOutreachAuthUrl();
            if (status === 200 && result?.outreachAuthUrl) {
              window.location.href = result.outreachAuthUrl;
              return;
            }
          }
        } catch (error) {
          console.error("Failed to get OAuth URL:", error);
        }
      };
      redirectToOAuth();
    }
  }, [signedInUser, organization]);

  const [loading, setLoading] = useState(false);
  const [shouldBlockConnectOutreach, setShouldBlockConnectOutreach] =
    useState(false);
  // HubSpot functionality excluded
  // const [shouldBlockConnectHubSpot, setShouldBlockConnectHubSpot] =
  //   useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<"outreach" | null>(null);

  const { isProcessing, url, error, openOutreachAuthUrl } =
    useConnectOutreach();
  // HubSpot functionality excluded
  // const { 
  //   isProcessing: isHubSpotProcessing, 
  //   url: hubSpotUrl, 
  //   error: hubSpotError, 
  //   openHubSpotAuthUrl 
  // } = useConnectHubSpot();

  const isConnectOutreachLoading =
    shouldBlockConnectOutreach || isProcessing || !url;
  // HubSpot functionality excluded
  // const isConnectHubSpotLoading =
  //   shouldBlockConnectHubSpot || isHubSpotProcessing || !hubSpotUrl;

  const shouldUseDefaultHeight = ![
    OnboardingSteps.Notice,
    OnboardingSteps.SetupBouncePreventing,
    OnboardingSteps.ChooseIntegration,
  ].includes(onboardingForm.currentStep);

  const {
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    trigger,
    formState: { errors },
  } = useForm<AccountSettings>({
    defaultValues: {
      userSettings: {
        firstName: signedInUser?.firstName || "",
        title: signedInUser?.title || "",
        profile: signedInUser?.profile || ProfileType.DEFAULT,
        phoneNumber: signedInUser?.phoneNumber || "",
      },
      organizationSettings: {
        timestampCustomField:
          organization?.timestampCustomField ||
          DEFAULT_CUSTOM_FIELD_IDS.timestampCustomFieldId,
        customField:
          organization?.customField ||
          DEFAULT_CUSTOM_FIELD_IDS.emailStatusCustomFieldId,
        enrichedCustomField:
          organization?.enrichedCustomField ||
          DEFAULT_CUSTOM_FIELD_IDS.enrichedCustomFieldId,
      },
    },
  });

  const customFieldValue = watch(
    "organizationSettings.customField"
  );

  const timestampCustomFieldValue = watch(
    "organizationSettings.timestampCustomField"
  );

  const enrichedCustomFieldValue = watch(
    "organizationSettings.enrichedCustomField"
  );

  const navigateToVideos = () =>
    window.open(`https://boringplugins.com/video`, "_blank");

  const closePopup = async () => {
    const customAuthToken = await generateCustomAuthToken();

    if (window.opener) {
      window.opener.postMessage(
        {
          type: "close",
          token: customAuthToken,
        },
        window.location.origin
      );
      window.close();
    }
  };

  const handleSecondaryBtnClick = (params?: {
    skipStepOnSuccess?: boolean;
  }) => {
    const { skipStepOnSuccess } = params || {};

    if (onboardingForm.currentStep > OnboardingSteps.ChooseIntegration) {
      setOnboardingForm((onboardingForm) => ({
        ...onboardingForm,
        currentStep: onboardingForm.currentStep - (skipStepOnSuccess ? 2 : 1),
      }));
    }
  };

  const handlePrimaryBtnClick = async (params?: {
    skipStepOnSuccess?: boolean;
  }) => {
    const { skipStepOnSuccess } = params || {};

    if (onboardingForm.currentStep < OnboardingSteps.WatchVideos) {
      setOnboardingForm((onboardingForm) => ({
        ...onboardingForm,
        currentStep: onboardingForm.currentStep + (skipStepOnSuccess ? 2 : 1),
        isOutreachAdmin:
          onboardingForm.currentStep === OnboardingSteps.ConfirmOutreachAdmin
            ? !!skipStepOnSuccess
            : onboardingForm.isOutreachAdmin,
      }));
    }
  };

  const onSubmit: SubmitHandler<AccountSettings> = async (
    data: AccountSettings
  ) => {
    if (!signedInUser || !organization) return;

    setLoading(true);

    try {
      const updatedSettings: AccountSettings = {
        ...data,
        organizationSettings:
          convertAppOrganizationSettingsToDbOrganizationSettings({
            ...data.organizationSettings,
            allowedOwnerEmails: onboardingForm.allowedOwnerEmails,
            timestampCustomField:
              data.organizationSettings.timestampCustomField,
            customField:
              data.organizationSettings.customField,
            enrichedCustomField: data.organizationSettings.enrichedCustomField,
            isEnrichedCustomFieldEnabled: true,
            isTimestampCustomFieldEnabled: true,
            isCustomFieldEnabled: true,
            isFinishedOnboarding: true,
          }),
      };

      await updateOrganizationSettings(
        removeUndefinedAndNull<AccountSettings>(updatedSettings)
      );

      const [organizationResponse, dbUser] = await Promise.all([
        getMyOrganization(),
        getDbUser(signedInUser.uid),
      ]);

      const updatedUser = convertDbAccountToAppAccount(
        dbUser,
        organizationResponse.result
      );

      setOrganization(
        convertDbOrganizationSettingsToAppOrganizationSettings(
          organizationResponse.result || {}
        )
      );
      setSignedInUser(updatedUser);
      setOnboardingForm((onboardingForm) => ({
        ...onboardingForm,
        currentStep: onboardingForm.currentStep + 1,
      }));
    } catch (error) {
      console.error("Failed to update settings:", error?.toString());
    } finally {
      setLoading(false);
    }
  };

  // HubSpot functionality excluded
  // const handleSelectHubSpot = () => {
  //   setSelectedIntegration("hubspot");
  //   setOnboardingForm((onboardingForm) => ({
  //     ...onboardingForm,
  //     currentStep: OnboardingSteps.ConnectHubSpot,
  //   }));
  // };

  const handleSelectOutreach = () => {
    setSelectedIntegration("outreach");
    // Skip admin check - go straight to connecting Outreach
    // We'll get the org ID from the API after they authenticate
    setOnboardingForm((onboardingForm) => ({
      ...onboardingForm,
      currentStep: OnboardingSteps.ConnectOutreach,
    }));
  };

  const steps: StepProps[] = [
    {
      component: (
        <ChooseIntegration
          // onSelectHubSpot={handleSelectHubSpot} // HubSpot functionality excluded
          onSelectOutreach={handleSelectOutreach}
        />
      ),
    },
    {
      component: <OutreachAdminPermissions />,
      secondaryButtonProps: {
        onClick: () =>
          handlePrimaryBtnClick({
            skipStepOnSuccess: true,
          }),
        children: "Yes",
        variant: ButtonVariants.brand,
      },
      primaryButtonProps: {
        onClick: () => handlePrimaryBtnClick(),
        children: "No",
        variant: ButtonVariants.light,
      },
    },
    {
      component: <ContactAdmin />,
      secondaryButtonProps: {
        onClick: () => handleSecondaryBtnClick(),
        children: "I understand",
        variant: ButtonVariants.light,
      },
      backButtonProps: {
        onClick: () => handleSecondaryBtnClick(),
      },
    },
    {
      component: <ConnectOutreach />,
      footer: <FormHelper helperText={error} severity={Severity.error} />,
      primaryButtonProps: {
        onClick: () => {
          setShouldBlockConnectOutreach(true);
          openOutreachAuthUrl();
        },
        type: "button",
        children: "Connect",
        variant: ButtonVariants.brand,
        loading: isConnectOutreachLoading,
        disabled: isConnectOutreachLoading,
      },
      backButtonProps: {
        onClick: () =>
          handleSecondaryBtnClick({
            skipStepOnSuccess: onboardingForm.isOutreachAdmin,
          }),
      },
    },
    // HubSpot functionality excluded
    // {
    //   component: <ConnectHubSpot />,
    //   footer: <FormHelper helperText={hubSpotError} severity={Severity.error} />,
    //   primaryButtonProps: {
    //     onClick: () => {
    //       setShouldBlockConnectHubSpot(true);
    //       openHubSpotAuthUrl();
    //     },
    //     type: "button",
    //     children: "Connect",
    //     variant: ButtonVariants.brand,
    //     loading: isConnectHubSpotLoading,
    //     disabled: isConnectHubSpotLoading,
    //   },
    //   backButtonProps: {
    //     onClick: () => {
    //       setOnboardingForm((onboardingForm) => ({
    //         ...onboardingForm,
    //         currentStep: OnboardingSteps.ChooseIntegration,
    //       }));
    //     },
    //   },
    // },
    {
      component: <BouncePreventing />,
      primaryButtonProps: {
        onClick: () => {
          // Skip this step entirely for HubSpot users (bounce prevention is Outreach-specific)
          // HubSpot functionality excluded - checking only organization/user flags
          const isHubSpotUser = 
            organization?.initialIntegrationType === "hubspot" ||
            signedInUser?.isHubSpotAuthorized;
          
          if (isHubSpotUser) {
            handlePrimaryBtnClick({ skipStepOnSuccess: true });
          } else {
            handlePrimaryBtnClick({
              skipStepOnSuccess:
                onboardingForm.bouncePrevention === BouncePrevention.DoNothing,
            });
          }
        },
        children: "Next",
        variant: ButtonVariants.brand,
      },
      backButtonProps: {
        onClick: () => handleSecondaryBtnClick(),
      },
    },
    {
      component: <Notice />,
      secondaryButtonProps: {
        onClick: () => handlePrimaryBtnClick(),
        children: "I understand",
        variant: ButtonVariants.light,
      },
      backButtonProps: {
        onClick: () => handleSecondaryBtnClick(),
      },
    },
    {
      component: <InviteUsers />,
      // secondaryButtonProps: {
      //   onClick: () => handlePrimaryBtnClick(),
      //   children: "I'll do this later",
      //   variant: ButtonVariants.light,
      // },
      primaryButtonProps: {
        onClick: () => handlePrimaryBtnClick(),
        children: "Next",
        disabled: loading,
        loading,
        variant: ButtonVariants.brand,
      },
    },
    {
      component: (
        <EmailStatus
          control={control}
          errors={errors}
          timestampCustomFieldValue={timestampCustomFieldValue}
          enrichedCustomFieldValue={enrichedCustomFieldValue}
          setValue={setValue}
        />
      ),
      secondaryButtonProps: {
        onClick: () => {
          if (
            customFieldValue !== organization?.customField
          ) {
            setValue(
              "organizationSettings.customField",
              organization?.customField
            );
          }

          handlePrimaryBtnClick();
        },
        children: "I'll do this later",
        variant: ButtonVariants.light,
      },
      primaryButtonProps: {
        onClick: async () => {
          const isValid = await trigger(
            "organizationSettings.customField"
          );

          if (isValid) {
            handlePrimaryBtnClick();
          }
        },
        children: "Next",
        disabled: loading,
        loading,
        variant: ButtonVariants.brand,
      },
    },
    {
      component: (
        <VerificationDate
          control={control}
          errors={errors}
          customFieldValue={customFieldValue}
          enrichedCustomFieldValue={enrichedCustomFieldValue}
          setValue={setValue}
        />
      ),
      secondaryButtonProps: {
        onClick: () => {
          if (
            timestampCustomFieldValue !== organization?.timestampCustomField
          ) {
            setValue(
              "organizationSettings.timestampCustomField",
              organization?.timestampCustomField
            );

            handlePrimaryBtnClick();
          }
        },
        children: "I'll do this later",
        variant: ButtonVariants.light,
      },
      primaryButtonProps: {
        onClick: async () => {
          const isValid = await trigger(
            "organizationSettings.enrichedCustomField"
          );

          if (isValid) {
            handlePrimaryBtnClick();
          }
        },
        children: "Next",
        disabled: loading,
        loading,
      },
      backButtonProps: {
        onClick: () => handleSecondaryBtnClick(),
      },
    },
    {
      component: (
        <EnrichedByBoring
          control={control}
          errors={errors}
          customFieldValue={customFieldValue}
          timestampCustomFieldValue={timestampCustomFieldValue}
          setValue={setValue}
        />
      ),
      secondaryButtonProps: {
        onClick: () => {
          handlePrimaryBtnClick();
        },
        children: "I'll do this later",
        variant: ButtonVariants.light,
      },
      primaryButtonProps: {
        onClick: async () => {
          const isValid = await trigger(
            "organizationSettings.enrichedCustomField"
          );

          if (isValid) {
            handlePrimaryBtnClick();
          }
        },
        disabled: loading,
        loading,
        children: "Next",
        variant: ButtonVariants.brand,
      },
      backButtonProps: {
        onClick: () => handleSecondaryBtnClick(),
      },
    },
    {
      component: <WatchVideos />,
      secondaryButtonProps: {
        onClick: () => closePopup(),
        children: "I've seen it",
        variant: ButtonVariants.light,
      },
      primaryButtonProps: {
        onClick: () => {
          closePopup();
          navigateToVideos();
        },
        children: "Watch the video",
        variant: ButtonVariants.brand,
      },
      backButtonProps: {
        onClick: () => handleSecondaryBtnClick(),
      },
    },
  ];

  useEffect(() => {
    reset({
      userSettings: {
        firstName: signedInUser?.firstName || "",
        title: signedInUser?.title || "",
        profile: signedInUser?.profile || ProfileType.DEFAULT,
        phoneNumber: signedInUser?.phoneNumber || "",
      },
      organizationSettings: {
        timestampCustomField:
          organization?.timestampCustomField ||
          DEFAULT_CUSTOM_FIELD_IDS.timestampCustomFieldId,
        customField:
          organization?.customField ||
          DEFAULT_CUSTOM_FIELD_IDS.emailStatusCustomFieldId,
        enrichedCustomField:
          organization?.enrichedCustomField ||
          DEFAULT_CUSTOM_FIELD_IDS.enrichedCustomFieldId,
      },
    });
  }, [signedInUser, organization, reset]);

  // Skip ChooseIntegration step if user already has an integration or organization has initialIntegrationType
  useEffect(() => {
    if (
      onboardingForm.currentStep === OnboardingSteps.ChooseIntegration &&
      (signedInUser?.isOutreachAuthorized || 
       signedInUser?.isHubSpotAuthorized ||
       organization?.initialIntegrationType)
    ) {
      // Determine which integration path to take
      // HubSpot functionality excluded - skip HubSpot onboarding
      if (organization?.initialIntegrationType === "hubspot" || signedInUser?.isHubSpotAuthorized) {
        // setSelectedIntegration("hubspot"); // HubSpot functionality excluded
        // setOnboardingForm((onboardingForm) => ({
        //   ...onboardingForm,
        //   currentStep: OnboardingSteps.ConnectHubSpot,
        // }));
        // Skip HubSpot onboarding for now
        console.warn("HubSpot integration detected but onboarding is not available");
      } else if (organization?.initialIntegrationType === "outreach" || signedInUser?.isOutreachAuthorized) {
        setSelectedIntegration("outreach");
        setOnboardingForm((onboardingForm) => ({
          ...onboardingForm,
          currentStep: OnboardingSteps.ConnectOutreach,
        }));
      }
    }
  }, [
    onboardingForm.currentStep,
    setOnboardingForm,
    signedInUser?.isOutreachAuthorized,
    signedInUser?.isHubSpotAuthorized,
    organization?.initialIntegrationType,
  ]);

  // Auto-skip Outreach-specific steps for HubSpot users
  useEffect(() => {
    // HubSpot functionality excluded - checking only organization/user flags
    const isHubSpotUser = 
      organization?.initialIntegrationType === "hubspot" ||
      signedInUser?.isHubSpotAuthorized;
    
    if (isHubSpotUser) {
      // Skip Outreach-specific steps
      if (onboardingForm.currentStep === OnboardingSteps.ConfirmOutreachAdmin ||
          onboardingForm.currentStep === OnboardingSteps.ContactOutreachAdmin ||
          onboardingForm.currentStep === OnboardingSteps.ConnectOutreach ||
          onboardingForm.currentStep === OnboardingSteps.SetupBouncePreventing) {
        setOnboardingForm((onboardingForm) => ({
          ...onboardingForm,
          currentStep: OnboardingSteps.Notice,
        }));
      }
    }
  }, [
    onboardingForm.currentStep,
    setOnboardingForm,
    selectedIntegration,
    organization?.initialIntegrationType,
    signedInUser?.isHubSpotAuthorized,
  ]);


  useEffect(() => {
    if (
      (signedInUser?.isOutreachAuthorized || signedInUser?.isHubSpotAuthorized) &&
      onboardingForm.currentStep < OnboardingSteps.InviteUsers &&
      onboardingForm.currentStep !== OnboardingSteps.ChooseIntegration
    ) {
      setOnboardingForm((onboardingForm) => ({
        ...onboardingForm,
        currentStep: OnboardingSteps.InviteUsers,
      }));
      return;
    }
  }, [
    onboardingForm.currentStep,
    setOnboardingForm,
    signedInUser?.isOutreachAuthorized,
    signedInUser?.isHubSpotAuthorized,
  ]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="d-flex align-items-center justify-content-center w-100 h-100"
    >
      <StepperWrapper shouldUseDefaultHeight={shouldUseDefaultHeight}>
        <Stepper
          steps={steps}
          currentStep={onboardingForm.currentStep}
          onCurrentStepChange={(newStep) =>
            setOnboardingForm({ ...onboardingForm, currentStep: newStep })
          }
        />
      </StepperWrapper>
    </form>
  );
};

export default Onboarding;
